import React, { useState } from 'react';
import type { Agent, AgentModel, AgentTool, AgentMemory, CreateAgentInput } from '../../lib/agent-types';

interface Props {
  initialData?: Partial<Agent>;
  onSubmit: (data: CreateAgentInput) => void;
  onCancel?: () => void;
  loading?: boolean;
}

const MODELS: AgentModel[] = ['gpt-4', 'gpt-4-turbo', 'gpt-3.5-turbo', 'claude-3-opus', 'claude-3-sonnet', 'claude-3-haiku'];
const TOOLS: AgentTool[] = ['web-search', 'web-fetch', 'code-interpreter', 'file-system', 'database', 'api-client'];
const MEMORY_OPTIONS: AgentMemory[] = ['none', 'short-term', 'long-term', 'vector-store'];

export function AgentForm({ initialData, onSubmit, onCancel, loading }: Props) {
  const [name, setName] = useState(initialData?.name || '');
  const [description, setDescription] = useState(initialData?.description || '');
  const [instructions, setInstructions] = useState(initialData?.instructions || '');
  const [model, setModel] = useState<AgentModel>(initialData?.model || 'claude-3-sonnet');
  const [tools, setTools] = useState<AgentTool[]>(initialData?.tools || []);
  const [memory, setMemory] = useState<AgentMemory>(initialData?.memory || 'none');
  const [published, setPublished] = useState(initialData?.published || false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({ name, description, instructions, model, tools, memory, published });
  };

  const toggleTool = (tool: AgentTool) => {
    setTools(prev => prev.includes(tool) ? prev.filter(t => t !== tool) : [...prev, tool]);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Live Preview */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg p-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-2">Live Preview</h3>
        <div className="bg-white rounded-md p-3 shadow-sm">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-lg font-bold text-gray-900">{name || 'Untitled Agent'}</span>
            {published && <span className="px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded">Published</span>}
          </div>
          <p className="text-sm text-gray-600 mb-2">{description || 'No description'}</p>
          <div className="flex gap-2 flex-wrap">
            <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded">{model}</span>
            <span className="px-2 py-0.5 text-xs bg-purple-100 text-purple-700 rounded">{memory} memory</span>
            {tools.map(t => <span key={t} className="px-2 py-0.5 text-xs bg-gray-100 text-gray-700 rounded">{t}</span>)}
          </div>
        </div>
      </div>

      {/* Name */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Agent Name *</label>
        <input
          type="text"
          value={name}
          onChange={e => setName(e.target.value)}
          maxLength={50}
          required
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          placeholder="My Awesome Agent"
        />
      </div>

      {/* Description */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
        <textarea
          value={description}
          onChange={e => setDescription(e.target.value)}
          maxLength={500}
          rows={2}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          placeholder="What does this agent do?"
        />
      </div>

      {/* Instructions */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Instructions *</label>
        <textarea
          value={instructions}
          onChange={e => setInstructions(e.target.value)}
          maxLength={10000}
          rows={6}
          required
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
          placeholder="You are a helpful assistant that..."
        />
      </div>

      {/* Model */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Model</label>
        <select
          value={model}
          onChange={e => setModel(e.target.value as AgentModel)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
        >
          {MODELS.map(m => <option key={m} value={m}>{m}</option>)}
        </select>
      </div>

      {/* Tools */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Tools</label>
        <div className="flex flex-wrap gap-2">
          {TOOLS.map(tool => (
            <button
              key={tool}
              type="button"
              onClick={() => toggleTool(tool)}
              className={`px-3 py-1.5 text-sm rounded-full border ${
                tools.includes(tool)
                  ? 'bg-blue-100 border-blue-300 text-blue-700'
                  : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
              }`}
            >
              {tool}
            </button>
          ))}
        </div>
      </div>

      {/* Memory */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Memory</label>
        <select
          value={memory}
          onChange={e => setMemory(e.target.value as AgentMemory)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
        >
          {MEMORY_OPTIONS.map(m => <option key={m} value={m}>{m}</option>)}
        </select>
      </div>

      {/* Published toggle */}
      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          id="published"
          checked={published}
          onChange={e => setPublished(e.target.checked)}
          className="w-4 h-4 text-blue-600 rounded"
        />
        <label htmlFor="published" className="text-sm text-gray-700">Publish to registry</label>
      </div>

      {/* Actions */}
      <div className="flex gap-3">
        <button
          type="submit"
          disabled={loading}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
        >
          {loading ? 'Saving...' : initialData?.id ? 'Update Agent' : 'Create Agent'}
        </button>
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
          >
            Cancel
          </button>
        )}
      </div>
    </form>
  );
}
